import { useMemo } from 'react';

import {
  ExternalCell,
  ExternalRow,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { formatPercentage } from '@cast/utils';

import { CopyToClipboard } from 'components/clickable-actions';

import { DetailsRowContainer, DetailWitTitle } from './DetailsRowContainer';
import { CPUPressureEvent } from '../../../types';

export const CPUPressureEventSubRow = (event: CPUPressureEvent) => {
  const { eligiblePods, totalPods } = useMemo(
    () => ({
      eligiblePods: event.details.containers.reduce(
        (acc, cur) => acc + cur.eligiblePodCount,
        0
      ),
      totalPods: event.details.containers.reduce(
        (acc, cur) => acc + cur.podCountTotal,
        0
      ),
    }),
    [event.details.containers]
  );
  return (
    <DetailsRowContainer event={event}>
      {/* Top section with PODS and MESSAGE placeholder fields */}
      <ExternalRow
        height={16}
        top={-12}
        sx={{
          borderTopColor: 'transparent !important',
          alignItems: 'flex-start',
        }}
      >
        <ExternalCell startColumn={0}>
          <DetailWitTitle
            title="Pods:"
            detail={
              <Typography
                variant="P10R"
                color={tokens.theme.fg.structural.secondary}
              >
                {eligiblePods}/{totalPods}
              </Typography>
            }
            titleVariant="light"
          />
        </ExternalCell>
      </ExternalRow>
      {/* Bottom section: CONTAINERS table */}
      {!!event.details.containers?.length && (
        <>
          <ExternalRow
            height={36}
            typography="L10B"
            color="grey.700"
            sx={{
              borderTop: '1px solid',
              borderColor: tokens.theme.border.structural.neutral,
            }}
          >
            <ExternalCell>Container</ExternalCell>
            <ExternalCell startColumn={1}>Stall %</ExternalCell>
          </ExternalRow>

          {event.details.containers.map(
            ({ name, pressurePercentage, eligiblePodCount, podCountTotal }) => (
              <ExternalRow key={name} height={28}>
                <ExternalCell
                  startColumn={0}
                  cellProps={{ typography: 'P12R' }}
                >
                  <Stack direction="row" gap={4} alignItems="center">
                    <CopyToClipboard text={name} iconSize={16} />
                    {name}
                  </Stack>
                </ExternalCell>
                <ExternalCell
                  startColumn={1}
                  cellProps={{ typography: 'P12R' }}
                >
                  <Stack direction="row" gap={4} alignItems="center">
                    <Typography variant="inherit">
                      {formatPercentage(pressurePercentage)}
                    </Typography>
                    <Typography variant="P10R" color="textSecondary">
                      ({eligiblePodCount}/{podCountTotal} pods)
                    </Typography>
                  </Stack>
                </ExternalCell>
              </ExternalRow>
            )
          )}
        </>
      )}
    </DetailsRowContainer>
  );
};
